import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChatBubbleOvalLeftIcon,
  HeartIcon,
  BookmarkIcon,
  ShareIcon,
  ChevronRightIcon,
  EyeIcon,
  ClockIcon,
  PlusIcon,
  PaperAirplaneIcon,
  FaceSmileIcon,
  PhotoIcon,
  PaperClipIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FlagIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { 
  HeartIcon as HeartSolidIcon, 
  BookmarkIcon as BookmarkSolidIcon,
  FireIcon as FireSolidIcon 
} from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ThreadDetail = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReply, setNewReply] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [replyTo, setReplyTo] = useState(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [savedPosts, setSavedPosts] = useState(new Set());

  useEffect(() => {
    // Sample thread data
    const threadData = {
      id: parseInt(threadId),
      title: "New AI-Powered Diagnostic Tools: Revolutionary Impact on Medical Imaging",
      content: `Recent advances in artificial intelligence are transforming how we approach medical diagnostics, particularly in radiology and pathology. I wanted to share some insights from our recent implementation of AI-powered diagnostic tools at our facility.

## Key Benefits We've Observed:

1. **Improved Accuracy**: Our AI system has shown 95% accuracy in detecting early-stage abnormalities
2. **Faster Processing**: Diagnostic time reduced from hours to minutes
3. **Enhanced Workflow**: Better integration with existing PACS systems
4. **Cost Effectiveness**: 30% reduction in diagnostic costs

## Implementation Challenges:

- Initial staff training requirements
- Integration with legacy systems
- Regulatory compliance considerations
- Data privacy and security protocols

I'd love to hear about your experiences with similar technologies and any best practices you've discovered.`,
      author: {
        id: 1,
        name: "Dr. Rajesh Kumar",
        avatar: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=50&h=50&fit=crop&crop=face",
        department: "Radiology",
        title: "Senior Radiologist",
        joinDate: "March 2020",
        postsCount: 156,
        reputation: 2847,
        badges: ["Expert", "Mentor", "AI Specialist"]
      },
      forum: {
        id: 1,
        name: "Medical Research & Innovation"
      },
      createdAt: "2024-07-23T08:30:00Z",
      views: 234,
      likes: 42,
      replies: 18,
      isPinned: true,
      isHot: true,
      isLocked: false,
      tags: ["AI", "Diagnostics", "Radiology", "Innovation"]
    };

    // Sample posts/replies data
    const postsData = [
      {
        id: 1,
        content: `This is fascinating, Dr. Kumar! We've been considering implementing similar AI diagnostic tools in our cardiology department. 

Could you share more details about the specific AI platform you're using? Also, how did you handle the initial resistance from senior staff who were skeptical about AI accuracy?

Our main concerns are:
- Integration with existing cardiac imaging systems
- Training requirements for our technicians
- Cost-benefit analysis over the first year

Any insights would be greatly appreciated!`,
        author: {
          id: 2,
          name: "Dr. Meera Sinha",
          avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=50&h=50&fit=crop&crop=face",
          department: "Cardiology",
          title: "Cardiologist",
          joinDate: "January 2021",
          postsCount: 89,
          reputation: 1456,
          badges: ["Contributor", "Heart Specialist"]
        },
        createdAt: "2024-07-23T09:15:00Z",
        likes: 12,
        replies: [
          {
            id: 11,
            content: `Great questions, Dr. Sinha! We used the MedAI Pro platform, which has excellent integration capabilities. For staff resistance, we started with a pilot program involving our most tech-savvy radiologists as champions.`,
            author: {
              id: 1,
              name: "Dr. Rajesh Kumar",
              avatar: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=50&h=50&fit=crop&crop=face",
              department: "Radiology",
              title: "Senior Radiologist"
            },
            createdAt: "2024-07-23T10:30:00Z",
            likes: 8
          }
        ]
      },
      {
        id: 2,
        content: `Excellent post! Our orthopedic department has had similar success with AI-assisted diagnosis for bone fractures and joint conditions.

Some additional benefits we've noticed:
- Reduced inter-observer variability
- Better detection of subtle fractures
- Improved resident training with AI as a teaching tool

One thing to consider is the importance of maintaining radiologist expertise alongside AI tools. We've found that AI works best as an augmentation tool rather than a replacement.`,
        author: {
          id: 3,
          name: "Dr. Amit Singh",
          avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
          department: "Orthopedic Surgery",
          title: "Chief of Orthopedics",
          joinDate: "August 2018",
          postsCount: 203,
          reputation: 3124,
          badges: ["Expert", "Department Head", "Bone Specialist"]
        },
        createdAt: "2024-07-23T11:45:00Z",
        likes: 18,
        replies: []
      },
      {
        id: 3,
        content: `This is the future of healthcare! I'm particularly interested in the regulatory compliance aspects you mentioned. 

We're in the early stages of evaluating AI diagnostic tools for our pathology lab. What regulatory frameworks did you need to navigate? Did you work directly with FDA representatives, or did you go through your AI vendor?

Also, how do you handle cases where AI recommendations differ significantly from radiologist assessments?`,
        author: {
          id: 4,
          name: "Dr. Priya Sharma",
          avatar: "https://images.unsplash.com/photo-1594824388853-5a7a6b1ab4b9?w=50&h=50&fit=crop&crop=face",
          department: "Pathology",
          title: "Pathologist",
          joinDate: "June 2019",
          postsCount: 67,
          reputation: 892,
          badges: ["Rising Star", "Lab Expert"]
        },
        createdAt: "2024-07-23T13:20:00Z",
        likes: 9,
        replies: [
          {
            id: 31,
            content: `For regulatory compliance, we worked closely with both our legal team and the AI vendor. The key is documenting everything and ensuring proper validation studies. Happy to share our compliance checklist offline!`,
            author: {
              id: 1,
              name: "Dr. Rajesh Kumar",
              avatar: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=50&h=50&fit=crop&crop=face",
              department: "Radiology",
              title: "Senior Radiologist"
            },
            createdAt: "2024-07-23T14:15:00Z",
            likes: 6
          },
          {
            id: 32,
            content: `Dr. Sharma, I'd recommend starting with the FDA's guidance documents on AI/ML-based medical devices. They're quite comprehensive and provide a good roadmap.`,
            author: {
              id: 5,
              name: "Sarah Wilson",
              avatar: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=50&h=50&fit=crop&crop=face",
              department: "Regulatory Affairs",
              title: "Regulatory Specialist"
            },
            createdAt: "2024-07-23T14:45:00Z",
            likes: 4
          }
        ]
      },
      {
        id: 4,
        content: `As someone who's been skeptical about AI in healthcare, I have to admit this implementation sounds impressive. My main concern has always been the "black box" nature of AI decisions.

How do you explain AI recommendations to patients? Do you find that patients are comfortable with AI-assisted diagnoses, or do they prefer traditional methods?

Also, from a liability perspective, how does AI factor into malpractice considerations?`,
        author: {
          id: 6,
          name: "Dr. Robert Kim",
          avatar: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=50&h=50&fit=crop&crop=face",
          department: "Internal Medicine",
          title: "Internist",
          joinDate: "February 2017",
          postsCount: 134,
          reputation: 1789,
          badges: ["Veteran", "Patient Advocate"]
        },
        createdAt: "2024-07-23T15:30:00Z",
        likes: 15,
        replies: []
      }
    ];

    // Simulate loading
    setTimeout(() => {
      setThread(threadData);
      setPosts(postsData);
      setLoading(false);
    }, 1000);
  }, [threadId]);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return time.toLocaleDateString();
  };

  const handleLikePost = (postId) => {
    const newLikedPosts = new Set(likedPosts);
    if (newLikedPosts.has(postId)) {
      newLikedPosts.delete(postId);
    } else {
      newLikedPosts.add(postId);
    }
    setLikedPosts(newLikedPosts);

    // Update post likes
    setPosts(prev => 
      prev.map(post => 
        post.id === postId 
          ? { ...post, likes: likedPosts.has(postId) ? post.likes - 1 : post.likes + 1 }
          : post
      )
    );
  };

  const handleSavePost = (postId) => {
    const newSavedPosts = new Set(savedPosts);
    if (newSavedPosts.has(postId)) {
      newSavedPosts.delete(postId);
    } else {
      newSavedPosts.add(postId);
    }
    setSavedPosts(newSavedPosts);
  };

  const handleReply = () => {
    if (newReply.trim()) {
      // Add new reply logic here
      console.log('New reply:', newReply);
      setNewReply('');
      setShowReplyForm(false);
      setReplyTo(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading discussion...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Thread Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-gray-500 mb-4 text-sm">
            <button 
              onClick={() => navigate('/forums')}
              className="hover:text-apollo-accent transition-colors"
            >
              Forums
            </button>
            <ChevronRightIcon className="w-4 h-4" />
            <button 
              onClick={() => navigate(`/forums/${thread.forum.id}`)}
              className="hover:text-apollo-accent transition-colors truncate max-w-32 sm:max-w-none"
            >
              {thread.forum.name}
            </button>
            <ChevronRightIcon className="w-4 h-4" />
            <span className="text-gray-900">Discussion</span>
          </div>

          {/* Thread Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6 space-y-4 sm:space-y-0">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {thread.isPinned && (
                  <span className="bg-apollo-accent text-white text-xs px-2 py-1 rounded-full">📌 Pinned</span>
                )}
                {thread.isHot && (
                  <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full flex items-center">
                    <FireSolidIcon className="w-3 h-3 mr-1" />
                    Hot Topic
                  </span>
                )}
                {thread.tags.map(tag => (
                  <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
              
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4">{thread.title}</h1>
              
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <EyeIcon className="w-4 h-4" />
                  <span>{thread.views} views</span>
                </div>
                <div className="flex items-center space-x-1">
                  <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                  <span>{thread.replies} replies</span>
                </div>
                <div className="flex items-center space-x-1">
                  <HeartIcon className="w-4 h-4" />
                  <span>{thread.likes} likes</span>
                </div>
                <div className="flex items-center space-x-1">
                  <ClockIcon className="w-4 h-4" />
                  <span>{formatTimeAgo(thread.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Thread Actions */}
            <div className="flex flex-wrap items-center gap-2 sm:ml-6">
              <button className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm">
                <HeartIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Like</span>
              </button>
              <button className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-apollo-accent hover:bg-apollo-accent/5 rounded-lg transition-colors text-sm">
                <BookmarkIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Save</span>
              </button>
              <button className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-apollo-accent hover:bg-apollo-accent/5 rounded-lg transition-colors text-sm">
                <ShareIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Thread Content and Replies */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Original Post */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Author Avatar & Info */}
            <div className="flex-shrink-0 sm:text-center">
              <div className="flex sm:flex-col items-center sm:items-center space-x-3 sm:space-x-0">
                <img
                  src={thread.author.avatar}
                  alt={thread.author.name}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full sm:mx-auto sm:mb-2"
                />
                <div className="text-sm sm:text-center">
                  <div className="font-medium text-gray-900">{thread.author.name}</div>
                  <div className="text-gray-500">{thread.author.title}</div>
                  <div className="text-gray-400 text-xs mt-1">{thread.author.department}</div>
                </div>
              </div>
              
              {/* Author Stats - Hidden on mobile */}
              <div className="hidden sm:block mt-3 text-xs text-gray-500 space-y-1">
                <div>Posts: {thread.author.postsCount}</div>
                <div>Rep: {thread.author.reputation.toLocaleString()}</div>
                <div>Joined: {thread.author.joinDate}</div>
              </div>

              {/* Author Badges - Hidden on mobile */}
              <div className="hidden sm:block mt-3 space-y-1">
                {thread.author.badges.map(badge => (
                  <span key={badge} className="inline-block bg-apollo-accent/10 text-apollo-accent text-xs px-2 py-0.5 rounded-full">
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Post Content */}
            <div className="flex-1">
              <div className="prose max-w-none">
                <div className="text-gray-800 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                  {thread.content}
                </div>
              </div>
              
              {/* Post Footer */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 sm:mt-6 pt-4 border-t border-gray-100 space-y-3 sm:space-y-0">
                <div className="flex flex-wrap items-center gap-4">
                  <button className="flex items-center space-x-1 text-gray-500 hover:text-red-500 transition-colors">
                    <HeartIcon className="w-4 h-4" />
                    <span className="text-sm">{thread.likes}</span>
                  </button>
                  <button className="flex items-center space-x-1 text-gray-500 hover:text-apollo-accent transition-colors">
                    <BookmarkIcon className="w-4 h-4" />
                    <span className="text-sm">Save</span>
                  </button>
                  <button className="flex items-center space-x-1 text-gray-500 hover:text-apollo-accent transition-colors">
                    <ShareIcon className="w-4 h-4" />
                    <span className="text-sm">Share</span>
                  </button>
                  <button className="flex items-center space-x-1 text-gray-500 hover:text-red-500 transition-colors">
                    <FlagIcon className="w-4 h-4" />
                    <span className="text-sm">Report</span>
                  </button>
                </div>
                <div className="text-sm text-gray-500">
                  Posted {formatTimeAgo(thread.createdAt)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Replies Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              Replies ({posts.length})
            </h2>
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="bg-gradient-to-r from-apollo-accent to-blue-600 text-white px-4 py-2 rounded-lg hover:from-apollo-accent/90 hover:to-blue-600/90 transition-colors font-medium flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Reply</span>
            </button>
          </div>

          {/* Reply Form */}
          {showReplyForm && (
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face"
                  alt="Your Avatar"
                  className="w-10 h-10 rounded-full sm:flex-shrink-0"
                />
                <div className="flex-1">
                  <textarea
                    value={newReply}
                    onChange={(e) => setNewReply(e.target.value)}
                    placeholder="Share your thoughts on this discussion..."
                    className="w-full p-3 sm:p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 min-h-24 sm:min-h-32 resize-none text-sm sm:text-base"
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 space-y-3 sm:space-y-0">
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-400 hover:text-apollo-accent transition-colors">
                        <FaceSmileIcon className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-apollo-accent transition-colors">
                        <PhotoIcon className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-apollo-accent transition-colors">
                        <PaperClipIcon className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setShowReplyForm(false);
                          setNewReply('');
                        }}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleReply}
                        className="bg-apollo-accent text-white px-4 py-2 rounded-lg hover:bg-apollo-accent/90 transition-colors font-medium flex items-center space-x-2 text-sm sm:text-base"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        <span>Post Reply</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Posts */}
          <div className="space-y-4 sm:space-y-6">
            {posts.map((post, index) => (
              <div key={post.id} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
                  {/* Author Info */}
                  <div className="flex-shrink-0 sm:text-center">
                    <div className="flex sm:flex-col items-center sm:items-center space-x-3 sm:space-x-0">
                      <img
                        src={post.author.avatar}
                        alt={post.author.name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full sm:mx-auto sm:mb-2"
                      />
                      <div className="text-sm sm:text-center">
                        <div className="font-medium text-gray-900">{post.author.name}</div>
                        <div className="text-gray-500 text-xs">{post.author.title}</div>
                      </div>
                    </div>
                    
                    {/* Author Stats - Hidden on mobile */}
                    {post.author.postsCount && (
                      <div className="hidden sm:block mt-2 text-xs text-gray-400 space-y-1">
                        <div>Posts: {post.author.postsCount}</div>
                        <div>Rep: {post.author.reputation?.toLocaleString()}</div>
                      </div>
                    )}

                    {/* Author Badges */}
                    {post.author.badges && (
                      <div className="mt-2 space-y-1">
                        {post.author.badges.map(badge => (
                          <span key={badge} className="inline-block bg-apollo-accent/10 text-apollo-accent text-xs px-2 py-0.5 rounded-full">
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Post Content */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-500">
                        Reply #{index + 1} • {formatTimeAgo(post.createdAt)}
                      </span>
                      <div className="flex items-center space-x-1">
                        <button className="p-1 text-gray-400 hover:text-apollo-accent transition-colors">
                          <ChevronUpIcon className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-apollo-accent transition-colors">
                          <ChevronDownIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="prose max-w-none">
                      <div className="text-gray-800 leading-relaxed whitespace-pre-line">
                        {post.content}
                      </div>
                    </div>

                    {/* Nested Replies */}
                    {post.replies && post.replies.length > 0 && (
                      <div className="mt-6 pl-4 border-l-2 border-gray-100">
                        {post.replies.map(reply => (
                          <div key={reply.id} className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="flex items-start space-x-3">
                              <img
                                src={reply.author.avatar}
                                alt={reply.author.name}
                                className="w-8 h-8 rounded-full"
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className="font-medium text-sm text-gray-900">{reply.author.name}</span>
                                  <span className="text-xs text-gray-500">{formatTimeAgo(reply.createdAt)}</span>
                                </div>
                                <div className="text-sm text-gray-800 leading-relaxed">
                                  {reply.content}
                                </div>
                                <div className="flex items-center space-x-3 mt-2">
                                  <button
                                    onClick={() => handleLikePost(reply.id)}
                                    className="flex items-center space-x-1 text-gray-500 hover:text-red-500 transition-colors"
                                  >
                                    <HeartIcon className="w-3 h-3" />
                                    <span className="text-xs">{reply.likes}</span>
                                  </button>
                                  <button className="text-xs text-gray-500 hover:text-apollo-accent transition-colors">
                                    Reply
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => handleLikePost(post.id)}
                          className={`flex items-center space-x-1 transition-colors ${
                            likedPosts.has(post.id) 
                              ? 'text-red-500' 
                              : 'text-gray-500 hover:text-red-500'
                          }`}
                        >
                          {likedPosts.has(post.id) ? (
                            <HeartSolidIcon className="w-4 h-4" />
                          ) : (
                            <HeartIcon className="w-4 h-4" />
                          )}
                          <span className="text-sm">{post.likes}</span>
                        </button>
                        <button
                          onClick={() => handleSavePost(post.id)}
                          className={`flex items-center space-x-1 transition-colors ${
                            savedPosts.has(post.id) 
                              ? 'text-apollo-accent' 
                              : 'text-gray-500 hover:text-apollo-accent'
                          }`}
                        >
                          {savedPosts.has(post.id) ? (
                            <BookmarkSolidIcon className="w-4 h-4" />
                          ) : (
                            <BookmarkIcon className="w-4 h-4" />
                          )}
                          <span className="text-sm">Save</span>
                        </button>
                        <button className="flex items-center space-x-1 text-gray-500 hover:text-apollo-accent transition-colors">
                          <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                          <span className="text-sm">Reply</span>
                        </button>
                        <button className="flex items-center space-x-1 text-gray-500 hover:text-red-500 transition-colors">
                          <FlagIcon className="w-4 h-4" />
                          <span className="text-sm">Report</span>
                        </button>
                      </div>
                      <button className="flex items-center space-x-1 text-gray-500 hover:text-apollo-accent transition-colors">
                        <UserPlusIcon className="w-4 h-4" />
                        <span className="text-sm">Follow</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Load More */}
        <div className="text-center">
          <button className="bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-50 transition-colors font-medium">
            Load More Replies
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ThreadDetail;
